import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { withBuildingScope } from "../ui/building-scope";

/** Desktop return management reuses the original scopes, U4 booking and accepted resolution commands. */
export function VisualInspectionReturns({
  assignment: a,
  latest,
  summary,
  disabled,
  onCommand,
}: {
  assignment: any;
  latest: any;
  summary: any;
  disabled: boolean;
  onCommand: (body: any, path: string) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]),
    scopeId = useRef(crypto.randomUUID());
  useEffect(() => {
    setSelected([]);
    scopeId.current = crypto.randomUUID();
  }, [a.id, summary.continuations.length]);
  const checks = latest
    ? a.definition.questions.filter(
        (q: any) =>
          q.type === "check" &&
          !q.condition &&
          ["Fail", "Unable to check"].includes(latest.answers[q.id]?.value),
      )
    : [];
  const label = (key: string) =>
    a.definition.questions.find((q: any) => q.id === key)?.label ??
    "Original inspection item";
  const photoIds = [
    ...new Set(
      selected.flatMap(
        (key) => a.definition.fieldProfile.checkCoveragePhotoIds[key] ?? [],
      ),
    ),
  ] as string[];
  if (!summary.canAuthorise && !summary.continuations.length) return null;
  return (
    <section aria-label="Inspection return visits">
      <h3>Return visits</h3>
      {summary.canAuthorise && checks.length > 0 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onCommand(
              {
                id: scopeId.current,
                submissionId: latest.id,
                questionIds: selected,
                reason: new FormData(e.currentTarget).get("reason"),
              },
              "continuations",
            );
          }}
        >
          <fieldset disabled={disabled}>
            <legend>Choose remaining checks</legend>
            <p>
              Authorise up to twenty exact checks. The original officer,
              evidence and due date stay on the original inspection. Operations
              will check an officer and time for the return.
            </p>
            {checks.map((q: any) => (
              <label key={q.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(q.id)}
                  disabled={!selected.includes(q.id) && selected.length >= 20}
                  onChange={(e) =>
                    setSelected((old) =>
                      e.target.checked
                        ? [...old, q.id]
                        : old.filter((key) => key !== q.id),
                    )
                  }
                />
                {q.label}
              </label>
            ))}
            {photoIds.length > 0 && (
              <p>
                The return also needs photographs for:{" "}
                {photoIds.map(label).join("; ")}.
              </p>
            )}
            <label>
              Why a return is needed
              <textarea name="reason" required minLength={3} maxLength={1000} />
            </label>
            <button disabled={!selected.length}>Authorise return checks</button>
          </fieldset>
        </form>
      )}
      {summary.continuations.map((c: any) => (
        <article key={c.id}>
          <h4>Authorised return</h4>
          <p>{c.question_ids.map(label).join("; ")}</p>
          {c.report_id ? (
            <>
              <Link
                to={withBuildingScope(
                  "/reports/" + c.report_id,
                  a.development_id,
                )}
              >
                Open return inspection
              </Link>
              <p>{c.status}</p>
              {c.question_ids.map((key: string) =>
                c.resolved_question_ids?.includes(key) ? (
                  <p key={key}>Original item resolved: {label(key)}</p>
                ) : summary.writesEnabled && c.can_resolve ? (
                  <form
                    key={key}
                    onSubmit={(e) => {
                      e.preventDefault();
                      onCommand(
                        {
                          scopeId: c.id,
                          questionId: key,
                          continuationSubmission: c.submission_id,
                          expectedContinuationVersion: c.report_version,
                          reason: new FormData(e.currentTarget).get("reason"),
                        },
                        "resolutions",
                      );
                    }}
                  >
                    <fieldset disabled={disabled}>
                      <legend>Resolve original item: {label(key)}</legend>
                      <label>
                        Why the accepted return resolves this item
                        <textarea
                          name="reason"
                          required
                          minLength={3}
                          maxLength={1000}
                        />
                      </label>
                      <button>Resolve from accepted return</button>
                    </fieldset>
                  </form>
                ) : null,
              )}
            </>
          ) : (
            <Link
              to={withBuildingScope("/operations/visits", a.development_id)}
            >
              Schedule the authorised return
            </Link>
          )}
        </article>
      ))}
    </section>
  );
}
